/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/invoiceDetails.scss';

import React, {MouseEventHandler, useState} from 'react';

import {useTranslation} from 'react-i18next';

import Invoice from '../../../models/invoiceModel';

import History from '../../../models/historyModel';

import {AwesomeIcon, Loader, parseMoney} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import OrderItem from '../../../models/orderItemModel';

import {BolmateButton} from '../../controls';

import {AccessKey, InvoiceType, VATType} from '../../../util/interfaces';

import useRight from '../../misc/useRight';

import {ensureNegative} from '../../../util/utils';

interface Props {
    onClickEdit: MouseEventHandler;
    onClose: MouseEventHandler;
    onClickDownload: MouseEventHandler;
    onClickSend: MouseEventHandler;
    onClickPreview: MouseEventHandler;
    invoice: Invoice;
    history: History[];
    loadingHistory: boolean;
    loadingInvoice: boolean;
}

export default function InvoiceDetails(props: Props) {
    const {t} = useTranslation();
    const [canEditCustomer, canEditItems] = useRight(AccessKey.INVOICE_EDIT_CUSTOMER, AccessKey.INVOICE_EDIT_ITEMS);
    const {
        invoice,
        onClose,
        onClickEdit,
        history,
        loadingHistory,
        onClickDownload,
        onClickPreview,
        onClickSend,
        loadingInvoice
    } = props;
    const [historyOpen, setHistoryOpen] = useState(false);

    function onToggleHistory() {
        setHistoryOpen(!historyOpen);
    }

    function renderActions() {
        return (
            <div className='actions-bar'>
                {invoice.invoiceType === InvoiceType.DEBET || !invoice.referenceInvoice ? (
                    <BolmateButton
                        onClick={onClose}
                        label={t('invoice.details.back', 'Terug')}
                        secondary
                        ornaments={{element: <AwesomeIcon name='arrow-left' />}}
                    />
                ) : (
                    <div />
                )}
                {canEditCustomer || canEditItems ? (
                    <BolmateButton
                        onClick={onClickEdit}
                        label={t('invoice.details.edit', 'Wijzigen')}
                        ornaments={{element: <AwesomeIcon name='pen' />}}
                    />
                ) : (
                    <div />
                )}
            </div>
        );
    }

    function renderHistory() {
        const histories = history.slice(0, historyOpen ? history.length : 3);
        return (
            <div className={classNames('block', 'invoice-history', historyOpen && 'open')}>
                <Loader active={loadingHistory} />
                <div className='header-container'>
                    <label className='header-title'>
                        <AwesomeIcon name='clock' prefix='far' />{' '}
                        {t('invoice.details.recentActivity', 'Recente activiteit')}
                    </label>
                </div>
                <div className='content'>
                    {renderHistoryItem(histories)}
                    {history.length > 3 && (
                        <div className='toggle-container'>
                            <label className='history-toggle' onClick={onToggleHistory}>
                                {historyOpen
                                    ? t('invoice.details.showLess', 'Minder historie weergeven')
                                    : t('invoice.details.showAll', 'Volledige historie weergeven')}
                            </label>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function renderHistoryItem(histories: History[], index?: number) {
        return (
            <div className='date-history' key={index}>
                {histories.map((history: History, index: number) => (
                    <div className='history' key={history.id}>
                        <AwesomeIcon name={history.actionIcon} className='action-icon' />
                        <div className='labels-container'>
                            <label className='action'>{history.translatedAction}</label>
                            <label className='date'>{history.dateMoment.format('DD-MM-YYYY HH:mm')}</label>
                            <label className='by'>{history.actionBy}</label>
                        </div>
                        {index < histories.length - 1 && <div className='connector' />}
                    </div>
                ))}
            </div>
        );
    }

    function renderInvoice() {
        return (
            <div className='block invoice'>
                <Loader active={loadingInvoice} />
                <div className='header-container'>
                    <label className='header-title'>{t('invoice.details.title', 'Details')}</label>
                    <div className='invoice-actions'>
                        {invoice.invoiceType === InvoiceType.CREDIT && (
                            <>
                                <BolmateButton
                                    onClick={onClickPreview}
                                    label={t('invoice.details.preview', 'Voorbeeld')}
                                    ornaments={{element: <AwesomeIcon name='eye' />}}
                                />
                                <BolmateButton
                                    onClick={onClickDownload}
                                    label={t('invoice.details.download', 'Downloaden')}
                                    ornaments={{element: <AwesomeIcon name='download' />}}
                                />
                                <BolmateButton
                                    onClick={onClickSend}
                                    label={t('invoice.details.send', 'Verzenden')}
                                    ornaments={{element: <AwesomeIcon name='paper-plane' />}}
                                />
                            </>
                        )}
                    </div>
                </div>
                <div className='content'>
                    <div className='details'>
                        <div className='details-container'>
                            <label className='details-title'>
                                {invoice.invoiceType === InvoiceType.DEBET
                                    ? t('invoice.details.invoice', 'Factuur')
                                    : t('invoice.details.creditInvoice', 'Creditfactuur')}
                            </label>
                            <label className='key'>{t('invoice.details.invoiceNo', 'Factuur nr.')}</label>
                            <label>{invoice.invoiceNum}</label>
                            <label className='key'>{t('invoice.details.orderNo', 'Order nr.')}</label>
                            <label>{invoice.orderId}</label>
                            <label className='key'>{t('invoice.details.orderPlaced', 'Bestelling geplaatst')}</label>
                            <label>{invoice.momentDate.format('DD MMMM YYYY')}</label>
                            <label className='key'>{t('invoice.details.invoiceDate', 'Factuurdatum')}</label>
                            <label>{invoice.invoiceDate.format('DD MMMM YYYY')}</label>
                            <label className='key'>{t('invoice.details.total', 'Totaal')}</label>
                            <label>&euro; {parseMoney(invoice.total)}</label>
                            <label className='key'>{t('invoice.details.items', 'Artikelen')}</label>
                            <label>{invoice.getNumProducts()}</label>
                            {invoice.invoiceType === InvoiceType.CREDIT && (
                                <>
                                    <label className='key'>{t('invoice.details.status', 'Status')}</label>
                                    <label>{invoice.translatedStatus}</label>
                                </>
                            )}
                        </div>
                        <div className='details-container'>
                            <label className='details-title'>{t('invoice.details.customer', 'Klant')}</label>
                            <label className='key'>{t('invoice.details.company', 'Bedrijf')}</label>
                            <label>{invoice.company}</label>
                            <label className='key'>{t('invoice.details.name', 'Naam')}</label>
                            <label>{invoice.fullName}</label>
                            <label className='key'>{t('invoice.details.address', 'Adres')}</label>
                            <label>{`${invoice.street} ${invoice.houseNumber}${
                                invoice.houseNumberSuffix.length ? ' ' + invoice.houseNumberSuffix : ''
                            }`}</label>
                            <div />
                            <label>
                                {invoice.zipCode}, {invoice.city}
                            </label>
                            <div />
                            <label>{invoice.country}</label>
                            <label className='key'>{t('invoice.details.emailLabel', 'E-mailadres')}</label>
                            <label>{invoice.email}</label>
                            <label className='key'>{t('invoice.details.cocLabel', 'KvK nummer')}</label>
                            <label>{invoice.cocNumber}</label>
                            <label className='key'>{t('invoice.details.vatLabel', 'BTW nummer')}</label>
                            <label>{invoice.vatNumber}</label>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function renderItems() {
        return (
            <div className='block items'>
                <Loader active={loadingInvoice} />
                <div className='header-container'>
                    <label className='header-title'>{t('invoice.details.products', 'Producten')}</label>
                </div>
                <div className='content'>
                    <div className='items'>
                        <div className='row head'>
                            <label>{t('invoice.details.col.quantity', 'AANTAL')}</label>
                            <label>{t('invoice.details.col.name', 'NAAM')}</label>
                            <label>{t('invoice.details.col.priceExcl', 'PRIJS excl. BTW')}</label>
                            <label>{t('invoice.details.col.vat', 'BTW')}</label>
                            <label className='right'>{t('invoice.details.col.totalExcl', 'TOTAAL excl. BTW')}</label>
                        </div>
                        {invoice.items.map(renderItem)}
                        {renderTotals()}
                    </div>
                </div>
            </div>
        );
    }

    function renderItem(item: OrderItem) {
        let vat: string;
        const vatType = item.vat.vat_type;
        if (vatType === VATType.REVERSED) vat = t('invoice.details.vatReversed', 'verlegd');
        else if (vatType === VATType.SPLIT) vat = t('invoice.details.vatCombi', 'Combi.');
        else if (vatType === VATType.MARGIN) vat = t('invoice.details.vatMargin', 'Marge');
        else if (vatType === VATType.KOR) vat = '';
        else vat = `${item.vat.value}%`;
        const cancelled = item.quantityCancelled;
        const notCancelled = item.quantityNotCancelled;
        const rows = [];
        if (notCancelled)
            rows.push(
                <div className='row' key={item.id}>
                    <label>{notCancelled}</label>
                    <label>{item.title}</label>
                    <label>&euro; {parseMoney(item.piecePriceExcl)}</label>
                    <label>{vat}</label>
                    <label className='right'>&euro; {parseMoney(item.piecePriceExcl * notCancelled)}</label>
                </div>
            );
        if (cancelled)
            rows.push(
                <div className='row cancelled' key={item.id + '-cancelled'}>
                    <label>{cancelled}</label>
                    <label>{item.title}</label>
                    <label>&euro; {parseMoney(item.piecePriceExcl)}</label>
                    <label>{vat}</label>
                    <label className='right'>&euro; {parseMoney(item.totalExcl)}</label>
                </div>
            );
        for (let ix = 0; ix < item.discounts.length; ix++) {
            const discount = item.discounts[ix];
            rows.push(
                <div className='row' key={`discount-${ix}`}>
                    <label>{1}</label>
                    <label>{discount.title}</label>
                    <label>&euro; {parseMoney(ensureNegative(discount.amount_excl))}</label>
                    <label>{vat}</label>
                    <label className='right'>&euro; {parseMoney(ensureNegative(discount.amount_excl))}</label>
                </div>
            );
        }
        return rows;
    }

    function renderTotals() {
        const {hasReversedCharge} = invoice;
        const vatAmounts = invoice.notCancelledVatAmount;
        const keys = Object.keys(vatAmounts).sort();
        return (
            <>
                <div className='row total mt'>
                    <label />
                    <label />
                    <label className='gray'>{t('invoice.details.totalExcl', 'Totaal excl. BTW')}</label>
                    <label />
                    <label className='right'>&euro; {parseMoney(invoice.totalNotCancelledExcl)}</label>
                </div>
                {hasReversedCharge ? (
                    <div className='row total'>
                        <label />
                        <label />
                        <label>{t('invoice.details.vatReversedFull', 'BTW verlegd')}</label>
                        <label />
                        <label />
                    </div>
                ) : (
                    keys.map((vatKey: string, index: number) => (
                        <div className='row total' key={index}>
                            <label />
                            <label />
                            <label className='gray'>
                                {t('invoice.details.vatLine', 'BTW')} ({vatKey})
                            </label>
                            <label />
                            <label className='right'>&euro; {parseMoney(vatAmounts[vatKey])}</label>
                        </div>
                    ))
                )}
                <div className='row bold total'>
                    <label />
                    <label />
                    <label>{t('invoice.details.totalIncl', 'Totaal incl. BTW')}</label>
                    <label />
                    <label className='right'>&euro; {parseMoney(invoice.total)}</label>
                </div>
            </>
        );
    }

    return (
        <div className='invoice-details'>
            {renderActions()}
            {renderHistory()}
            {renderInvoice()}
            {renderItems()}
        </div>
    );
}
